export enum LoggerCategoryEnum {
    default = 'default',
    analytics = 'analytics',
    diagnostic = 'diagnostic',
    api = 'api',
    ui = 'ui'
}

export enum LoggerLevelEnum {
    log = 'log',
    error = 'error',
    info = 'info',
    warn = 'warn',
    debug = 'debug'
}

/* Data format accepted on the logs */
export type LogDataType = {
    [key: string]: any;
};

/**
 * The logs will collect data in this interfaces given shape/format.
 */
export interface ILog {
    level: LoggerLevelEnum;
    message: string;
    data: LogDataType;
    parentAppStack: string[];
    category: LoggerCategoryEnum;
    timestamp: number;
    name: string;
    version: string;
    platform: string;
    environment: string;
}

/**
 * The interface for transport functions. Please see the main Logger page for examples on its usage and customisation.
 */
export interface ITransportFn {
    (logs: ILog[]): Promise<any>;
}

export interface IBrowserConsoleTransportFn extends ITransportFn {
    prototype: { isBrowserConsoleTransport: true };
}

export type TransportsType = ITransportFn | IBrowserConsoleTransportFn | (IBrowserConsoleTransportFn | ITransportFn)[];

export interface ICreateLoggerCore {
    /** Transport functions. */
    transports: {
        [LoggerCategoryEnum.default]: TransportsType;
        [LoggerCategoryEnum.analytics]: TransportsType;
        [LoggerCategoryEnum.diagnostic]: TransportsType;
        [LoggerCategoryEnum.api]: TransportsType;
        [LoggerCategoryEnum.ui]: TransportsType;
    };
    /** Should the logs be automatically posted. If false you will need to handle posting by calling `instance.postLogs()`. */
    autoPost?: boolean;
    /** The frequency in milliseconds which logs will be automatically posted. */
    autoPostFrequency?: number;
    /** The name of the application. */
    appName: string;
    /** The applications version number/hash/identifier. */
    appVersion: string;
    /** Setting the level at which you wish to collect logs. The levels in ascending order are: `[log, info, debug, warn, error]` so if you set for example a logLevel of warn, only warn and error logs will be captured by the queue. */
    logLevel?: LoggerLevelEnum;
    /** ParentAppStack defines the applications which are a parent of your application. This is only applicable in browser environments and you do not need to implement this when using an environment specific Logger. */
    parentAppStack?: string[];
    // the platform/environment, eg. nab-x, nab-one, ib...
    platform: string;
    // The environment the application is running within. eg, dev, prod...
    environment: string;
    // Enable/Disable sensitive masking in data objects
    enableMasking?: boolean;
    // Sensitive fields to be basked in data objects
    maskFields?: string[];
}

export interface ILogQueueLog {
    level: LoggerLevelEnum;
    message: string;
    data: LogDataType;
    category: LoggerCategoryEnum;
    timestamp: number;
}

export interface ILogQueue {
    [LoggerCategoryEnum.default]: ILogQueueLog[];
    [LoggerCategoryEnum.analytics]: ILogQueueLog[];
    [LoggerCategoryEnum.diagnostic]: ILogQueueLog[];
    [LoggerCategoryEnum.api]: ILogQueueLog[];
    [LoggerCategoryEnum.ui]: ILogQueueLog[];
}

export type LogFnType = (
    /** The category of the log. If a log message is given instead of a log category the log will be queued under the default category. */
    logCategoryOrMessage: LoggerCategoryEnum | LoggerCategoryEnum[] | string,
    /** The message to log. If the first parameter given is a message you should pass in a data object here. */
    messageOrData?: string | LogDataType,
    /** Data to store with the log. You can pass in any number of data objects and they will be merged (shallow with lodash) before being stored with the log. */
    ...data: LogDataType[]
) => void;

export interface ILogger {
    postLogs: (logCategory?: LoggerCategoryEnum) => void;
    destroy: () => void;
    log: LogFnType;
    warn: LogFnType;
    error: LogFnType;
    info: LogFnType;
    debug: LogFnType;
    consoleLevel: LoggerLevelEnum;
    consoleLevelValue: LoggerLevelEnum;
}
